"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";

type Direction = "increase" | "decrease" | "change";

interface Hypothesis {
  label: string;
  text: string;
}

/** English strings. A translated content file overrides these through `ui`. */
export const HYPOTHESIS_GENERATOR_STRINGS = {
  increase: "Increase",
  decrease: "Decrease",
  changeEitherWay: "Change (either way)",
  increaseVerb: "increase",
  decreaseVerb: "decrease",
  changeVerb: "change",
  increaseNoun: "an increase",
  decreaseNoun: "a decrease",
  changeNoun: "a measurable change",
  populationClause: " among {population}",
  ifThenLabel: "If/then hypothesis",
  ifThenText: "If {iv} is changed, then {dv} will {verb}{pop}.",
  directionalLabel: "Directional hypothesis (H1)",
  directionalText: "A change in {iv} will lead to {noun} in {dv}{pop}.",
  nonDirectionalLabel: "Non-directional hypothesis (H1)",
  nonDirectionalText: "There is a relationship between {iv} and {dv}{pop}.",
  nullLabel: "Null hypothesis (H0)",
  nullText:
    "There is no relationship between {iv} and {dv}{pop}; any observed difference in {dv} is due to chance.",
  independentVariableLabel: "Independent variable — what you change",
  ivPlaceholder: "e.g. hours of sleep per night",
  dependentVariableLabel: "Dependent variable — what you measure",
  dvPlaceholder: "e.g. test scores",
  populationLabel: "Population (optional) — who the study applies to",
  populationPlaceholder: "e.g. high school students",
  predictedDirection: "Predicted direction of the dependent variable",
  generateHypotheses: "Generate Hypotheses",
  resultsHeading:
    "Your hypothesis in four formats — refine the wording so each variable is precisely defined",
  copied: "Copied!",
  copy: "Copy",
} as const;

function buildHypotheses(
  iv: string,
  dv: string,
  population: string,
  direction: Direction,
  strings: typeof HYPOTHESIS_GENERATOR_STRINGS
): Hypothesis[] {
  const verbs: Record<Direction, string> = {
    increase: strings.increaseVerb,
    decrease: strings.decreaseVerb,
    change: strings.changeVerb,
  };
  const nouns: Record<Direction, string> = {
    increase: strings.increaseNoun,
    decrease: strings.decreaseNoun,
    change: strings.changeNoun,
  };
  const verb = verbs[direction];
  const noun = nouns[direction];
  const pop =
    population.length > 0 ? t(strings.populationClause, { population }) : "";
  const slots = { iv, dv, pop };
  return [
    {
      label: strings.ifThenLabel,
      text: t(strings.ifThenText, { ...slots, verb }),
    },
    {
      label: strings.directionalLabel,
      text: t(strings.directionalText, { ...slots, noun }),
    },
    {
      label: strings.nonDirectionalLabel,
      text: t(strings.nonDirectionalText, slots),
    },
    {
      label: strings.nullLabel,
      text: t(strings.nullText, slots),
    },
  ];
}

export default function HypothesisGeneratorWidget() {
  const [independentVariable, setIndependentVariable] = useState("");
  const [dependentVariable, setDependentVariable] = useState("");
  const [population, setPopulation] = useState("");
  const [direction, setDirection] = useState<Direction>("increase");
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const strings = useToolUi(HYPOTHESIS_GENERATOR_STRINGS);

  const directions: { value: Direction; label: string }[] = [
    { value: "increase", label: strings.increase },
    { value: "decrease", label: strings.decrease },
    { value: "change", label: strings.changeEitherWay },
  ];

  const ready =
    independentVariable.trim().length > 0 &&
    dependentVariable.trim().length > 0;

  function handleGenerate() {
    if (!ready) {
      return;
    }
    setHypotheses(
      buildHypotheses(
        independentVariable.trim(),
        dependentVariable.trim(),
        population.trim(),
        direction,
        strings
      )
    );
    setCopiedIndex(null);
  }

  function handleCopy(text: string, index: number) {
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  return (
    <>
      {/* Independent variable */}
      <div className="space-y-2">
        <label className="font-medium text-foreground text-sm" htmlFor="hyp-iv">
          {strings.independentVariableLabel}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hyp-iv"
          onChange={(e) => setIndependentVariable(e.target.value)}
          placeholder={strings.ivPlaceholder}
          type="text"
          value={independentVariable}
        />
      </div>

      {/* Dependent variable */}
      <div className="space-y-2">
        <label className="font-medium text-foreground text-sm" htmlFor="hyp-dv">
          {strings.dependentVariableLabel}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hyp-dv"
          onChange={(e) => setDependentVariable(e.target.value)}
          placeholder={strings.dvPlaceholder}
          type="text"
          value={dependentVariable}
        />
      </div>

      {/* Population */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="hyp-population"
        >
          {strings.populationLabel}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hyp-population"
          onChange={(e) => setPopulation(e.target.value)}
          placeholder={strings.populationPlaceholder}
          type="text"
          value={population}
        />
      </div>

      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.predictedDirection}
        </p>
        <div className="flex flex-wrap gap-3">
          {directions.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="hyp-direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!ready}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateHypotheses}
        </button>
      </div>

      {/* Results */}
      {hypotheses.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.resultsHeading}
          </h2>
          <ul className="space-y-3">
            {hypotheses.map((h, index) => (
              <li
                className="rounded-lg border border-border bg-card p-5"
                key={h.label}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-medium text-foreground text-sm">
                      {h.label}
                    </h3>
                    <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                      {h.text}
                    </p>
                  </div>
                  <button
                    className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                    onClick={() => handleCopy(h.text, index)}
                    type="button"
                  >
                    {copiedIndex === index ? strings.copied : strings.copy}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
